import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Form, Formik, FormikValues } from "formik";

import { bemClassName } from "../../utils/bemClassName";
import composeFieldValues from "./composeFieldValues";
import { joinClassNames } from "../../utils/joinClassNames";
import { useSearchQuestion, useSearchResults } from "../../context/DataContext";
import Facets from "./Facets";
import FacetToggle from "./FacetToggle";
import QueryField from "./QueryField";
import SearchFormContext from "../../context/SearchFormContext";

import "./SearchForm.css";
import styles from "./SearchForm.module.css";
import composeFormikValues from "./composeFormikValues";
import composeFormURLSearchParams from "./composeFormURLSearchParams";

export type CommonField = {
    disabled?: boolean;
    id: string;
    label: string;
    name: string;
    placeholder?: string;
};

export type SearchFieldFacetType =
    | "checkboxGroup"
    | "radioGroup"
    | "select"
    | "dateRange"
    | "multiSelect";

export type SearchFieldInput = CommonField & {
    type?: string;
};

export type SearchFieldOption = {
    id: string;
    label: string;
    value: string;
};

export type SearchFieldSelect = CommonField & {
    facetType: "select";
    options: SearchFieldOption[];
};

export type SearchFieldMultiSelect = CommonField & {
    facetType: "multiSelect";
    options: SearchFieldOption[];
};

export type SearchFieldCheckbox = CommonField & {
    facetType: "checkboxGroup";
    options: SearchFieldOption[];
};

export type SearchFieldRange = CommonField & {
    facetType: "dateRange";
    options: SearchFieldOption[];
    idStart?: string;
    idEnd?: string;
    nameStart?: string;
    nameEnd?: string;
};

export type SearchFieldRadio = CommonField & {
    facetType: "radioGroup";
    options: SearchFieldOption[];
};

export type SearchFieldsType = {
    query: SearchFieldInput;
    // Facets
    archived?: SearchFieldCheckbox;
    datePublished?: SearchFieldCheckbox;
    regionLocation?: SearchFieldSelect;
    resourceType?: SearchFieldSelect;
    status?: SearchFieldSelect;
    topic?: SearchFieldSelect;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
};

export interface SearchFormInitialValues {
    [key: string]: string | string[] | null | undefined;
    // Funnelback built-in params
    query: string;
    // Funnelback facets
    archived?: string[];
    datePublished?: string[];
    regionLocation?: string;
    resourceType?: string;
    topic?: string;
}

export interface SearchFormProps {
    facetBlockId: string;
    formAction: string;
    initialExpandFacets: boolean;
}

export const SearchForm = ({
    facetBlockId,
    formAction,
    initialExpandFacets,
}: SearchFormProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [expandFacets, setExpandFacets] = useState(initialExpandFacets);
    const { facets, allFacets } = useSearchResults();
    const { setFormQuestion } = useSearchQuestion();
    const facetProps = { id: facetBlockId };
    const facetToggleProps = { expandFacets, facetBlockId, setExpandFacets };
    const maybeFacets = expandFacets ? <Facets {...facetProps} /> : null;

    const handleSearch = (formValues: FormikValues): void => {
        const submittedSearchParams = composeFormURLSearchParams(
            formValues,
            allFacets
        );

        const facetParams: { [type: string]: string | string[] } = {};
        submittedSearchParams.forEach((paramValue, paramKey) => {
            // Do not submit unpopulated facets
            if (!paramValue.trim()) return;

            if (paramKey in facetParams) {
                const previousValue = facetParams[paramKey];
                if (typeof previousValue === "string") {
                    facetParams[paramKey] = [previousValue, paramValue];
                } else {
                    facetParams[paramKey] = [...previousValue, paramValue];
                }
            } else {
                facetParams[paramKey] = paramValue;
            }
        });

        // Request new data from Funnelback
        setFormQuestion(formValues.query, facetParams);
        // Set URL search params
        setSearchParams(submittedSearchParams);
    };

    const fieldsConfig = composeFieldValues(allFacets);

    const initialValues = composeFormikValues(searchParams, facets);
    const formConfig = { initialValues };

    const maybeForm =
        formConfig && !!fieldsConfig ? (
            <SearchFormContext.Provider value={fieldsConfig}>
                <div
                    className={joinClassNames([
                        bemClassName("find-a-panel", "search-bar"),
                        styles.formWrapper,
                    ])}
                >
                    <Formik {...formConfig} onSubmit={handleSearch}>
                        <Form
                            method="GET"
                            action={formAction}
                            className={styles.form}
                        >
                            <QueryField />
                            {facets.length > 0 && (
                                <FacetToggle {...facetToggleProps} />
                            )}
                            {maybeFacets}
                        </Form>
                    </Formik>
                    <hr className={styles.separator} />
                </div>
            </SearchFormContext.Provider>
        ) : null;

    return maybeForm;
};

export default SearchForm;
